import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  PunchItem, PunchStatus, PunchSeverity, PUNCH_VERIFIED_STATUS, Project,
} from '../../../types';
import {
  getProjectPunchItems, createPunchItem, updatePunchItem, deletePunchItem, nextItemNumber,
} from '../../../services/punchService';
import { exportPunchListPdf, PunchPdfGroupBy } from '../../../services/punchListPdfService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import ApprovalStagesSection from '../ApprovalStagesSection';
import {
  ApprovalStage, ApprovalEntityType, ApprovalStageStatus,
  findProjectChainForEntityType, getEntityStages,
  instantiateStagesForEntity, approveStage, rejectStage,
} from '../../../services/approvalService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const STATUS_LABEL: Record<number, string> = {
  [PunchStatus.Open]: 'Open',
  [PunchStatus.InProgress]: 'In Progress',
  [PunchStatus.ReadyForInspection]: 'Ready For Inspection',
  [PunchStatus.Closed]: 'Closed',
};
const STATUS_ORDER = [PunchStatus.Open, PunchStatus.InProgress, PunchStatus.ReadyForInspection, PunchStatus.Closed];
const STATUS_BADGE: Record<number, { bg: string; fg: string }> = {
  [PunchStatus.Open]:               { bg: '#FEE2E2', fg: '#991B1B' },
  [PunchStatus.InProgress]:         { bg: '#D4EEF5', fg: '#0A5568' },
  [PunchStatus.ReadyForInspection]: { bg: '#FEF3C7', fg: '#92400E' },
  [PunchStatus.Closed]:             { bg: '#D1FAE5', fg: '#065F46' },
};
const SEVERITY_LABEL: Record<number, string> = {
  [PunchSeverity.Minor]:    'Minor',
  [PunchSeverity.Major]:    'Major',
  [PunchSeverity.Critical]: 'Critical',
};
const SEVERITY_ORDER = [PunchSeverity.Critical, PunchSeverity.Major, PunchSeverity.Minor];
const SEVERITY_BADGE: Record<number, { bg: string; fg: string }> = {
  [PunchSeverity.Minor]:    { bg: '#F3F4F6', fg: '#5A6272' },
  [PunchSeverity.Major]:    { bg: '#FEF3C7', fg: '#92400E' },
  [PunchSeverity.Critical]: { bg: '#FEE2E2', fg: '#991B1B' },
};

// A punch item is "verified" once it reaches the terminal Closed status
// (inspection passed) — that is the state the verifiedby/verifiedon fields
// record. See PUNCH_VERIFIED_STATUS in types/index.ts.
export function isPunchVerified(p: PunchItem): boolean {
  return p.dagrun_pm_status === PUNCH_VERIFIED_STATUS || !!p.dagrun_pm_verifiedon;
}

type StatusFilter = 'all' | 'active' | number;
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: PunchStatus.Open, label: 'Open' },
  { value: PunchStatus.InProgress, label: 'In Progress' },
  { value: PunchStatus.ReadyForInspection, label: 'Ready' },
  { value: PunchStatus.Closed, label: 'Verified' },
];

type SeverityFilter = 'all' | number;

// Sortable columns. Each maps to a comparator value extractor.
type SortKey = 'item' | 'title' | 'location' | 'drawing' | 'trade' | 'assignee' | 'due' | 'severity' | 'status' | 'verified';
type SortDir = 'asc' | 'desc';

function sortValue(p: PunchItem, key: SortKey): string | number {
  switch (key) {
    case 'item':     return p.dagrun_pm_itemnumber ?? 0;
    case 'title':    return (p.dagrun_pm_name ?? '').toLowerCase();
    case 'location': return (p.dagrun_pm_location ?? '').toLowerCase();
    case 'drawing':  return (p.dagrun_pm_drawingreference ?? '').toLowerCase();
    case 'trade':    return (p.dagrun_pm_trade ?? '').toLowerCase();
    case 'assignee': return (p.dagrun_pm_assigneename ?? '').toLowerCase();
    case 'due':      return p.dagrun_pm_duedate ? new Date(p.dagrun_pm_duedate).getTime() : 0;
    case 'severity': return p.dagrun_pm_severity ?? 0;
    case 'status':   return p.dagrun_pm_status ?? 0;
    case 'verified': return isPunchVerified(p) ? 1 : 0;
  }
}

export default function PunchListTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_punchlist">
      <PunchListTabBody {...props} />
    </ModuleGuard>
  );
}

function PunchListTabBody({ pca, projectId, project }: Props) {
  const [items, setItems] = useState<PunchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [tradeFilter, setTradeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('item');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [drawerItem, setDrawerItem] = useState<PunchItem | 'new' | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const exportWrapRef = useRef<HTMLDivElement>(null);

  const reload = useCallback(() => {
    setLoading(true);
    return getProjectPunchItems(pca, projectId)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  // Close export menu on outside click.
  useEffect(() => {
    if (!exportOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (exportWrapRef.current && !exportWrapRef.current.contains(e.target as Node)) setExportOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [exportOpen]);

  const trades = useMemo(
    () => Array.from(new Set(items.map(i => i.dagrun_pm_trade).filter((t): t is string => !!t && t.trim().length > 0))).sort(),
    [items],
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'item' || key === 'title' ? 'asc' : 'desc'); }
  };

  const filtered = useMemo(() => {
    let out = items;
    if (statusFilter === 'active') out = out.filter(i => i.dagrun_pm_status !== PUNCH_VERIFIED_STATUS);
    else if (typeof statusFilter === 'number') out = out.filter(i => i.dagrun_pm_status === statusFilter);
    if (typeof severityFilter === 'number') out = out.filter(i => i.dagrun_pm_severity === severityFilter);
    if (tradeFilter !== 'all') out = out.filter(i => (i.dagrun_pm_trade ?? '') === tradeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(i =>
        (i.dagrun_pm_name ?? '').toLowerCase().includes(q) ||
        (i.dagrun_pm_location ?? '').toLowerCase().includes(q) ||
        (i.dagrun_pm_drawingreference ?? '').toLowerCase().includes(q));
    }
    const sorted = [...out].sort((a, b) => {
      const va = sortValue(a, sortKey), vb = sortValue(b, sortKey);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [items, statusFilter, severityFilter, tradeFilter, search, sortKey, sortDir]);

  const filtersActive = statusFilter !== 'all' || severityFilter !== 'all' || tradeFilter !== 'all' || !!search.trim();

  const runExport = (groupBy: PunchPdfGroupBy) => {
    setExportOpen(false);
    // Export the currently filtered + sorted set so what the user sees is what prints.
    exportPunchListPdf(filtered, project, groupBy);
  };

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Punch List</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div ref={exportWrapRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setExportOpen(o => !o)} disabled={items.length === 0}
              style={{
                background: '#fff', color: '#0E7490', border: '1px solid #0E7490',
                padding: '8px 14px', borderRadius: 6, fontFamily: 'var(--font-ui)',
                fontSize: 13, fontWeight: 600, cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                opacity: items.length === 0 ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              Export PDF <span style={{ fontSize: 9 }}>▾</span>
            </button>
            {exportOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4,
                background: '#fff', border: '1px solid #DDE0E6', borderRadius: 8,
                boxShadow: '0 4px 16px rgba(15,17,23,0.12)', minWidth: 180, zIndex: 60, padding: 4,
              }}>
                {([['location', 'Group by Location'], ['trade', 'Group by Trade'], ['status', 'Group by Status']] as [PunchPdfGroupBy, string][]).map(([g, label]) => (
                  <div key={g} onClick={() => runExport(g)}
                    style={{ padding: '8px 10px', fontSize: 13, color: '#0F1117', cursor: 'pointer', borderRadius: 6 }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >{label}</div>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={() => setDrawerItem('new')}
            style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
            onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
          >+ Add Punch Item</button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <FilterPills value={statusFilter} options={STATUS_FILTERS} onChange={setStatusFilter} />
        <select value={String(severityFilter)} onChange={e => setSeverityFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          style={{ ...BM_INPUT, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
          <option value="all">All severities</option>
          {SEVERITY_ORDER.map(s => <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>)}
        </select>
        <select value={tradeFilter} onChange={e => setTradeFilter(e.target.value)}
          style={{ ...BM_INPUT, width: 'auto', padding: '5px 8px', fontSize: 12 }}>
          <option value="all">All trades</option>
          {trades.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, location, drawing…"
          style={{ ...BM_INPUT, width: 240, padding: '5px 10px', fontSize: 12 }} />
        {filtersActive && (
          <button type="button" onClick={() => { setStatusFilter('all'); setSeverityFilter('all'); setTradeFilter('all'); setSearch(''); }}
            style={{ background: 'transparent', border: 'none', color: '#0E7490', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            × Clear
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>
          Showing {filtered.length} of {items.length}
        </span>
      </div>

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ color: '#9198A8', fontSize: 14, marginBottom: 12 }}>
            {items.length === 0 ? 'No punch items yet — add the first one.' : 'No punch items match these filters.'}
          </div>
          {items.length === 0 && (
            <button type="button" onClick={() => setDrawerItem('new')}
              style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              + Add Punch Item
            </button>
          )}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <SortableTh label="#" col="item" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Title" col="title" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Location" col="location" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Drawing Ref" col="drawing" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Trade" col="trade" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Assigned To" col="assignee" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Due" col="due" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Severity" col="severity" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Verified" col="verified" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          </tr></thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.dagrun_pm_punchitemid} onClick={() => setDrawerItem(p)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#9198A8' }}>{p.dagrun_pm_itemnumber ?? '—'}</span></Td>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{p.dagrun_pm_name}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{p.dagrun_pm_location || <Em />}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>{p.dagrun_pm_drawingreference || <Em />}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{p.dagrun_pm_trade || <Em />}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{p.dagrun_pm_assigneename || <Em />}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {p.dagrun_pm_duedate ? new Date(p.dagrun_pm_duedate).toLocaleDateString() : '—'}
                </span></Td>
                <Td>{p.dagrun_pm_severity != null
                  ? <Badge {...SEVERITY_BADGE[p.dagrun_pm_severity]}>{SEVERITY_LABEL[p.dagrun_pm_severity]}</Badge>
                  : <Em />}</Td>
                <Td onClick={e => e.stopPropagation()}>
                  <select
                    value={p.dagrun_pm_status ?? PunchStatus.Open}
                    onChange={async e => {
                      const v = Number(e.target.value);
                      const patch: Record<string, unknown> = { dagrun_pm_status: v };
                      // Reaching the verified (Closed) state stamps verifiedon if not already set.
                      if (v === PUNCH_VERIFIED_STATUS && !p.dagrun_pm_verifiedon) patch.dagrun_pm_verifiedon = new Date().toISOString();
                      await updatePunchItem(pca, p.dagrun_pm_punchitemid, patch);
                      setItems(prev => prev.map(x => x.dagrun_pm_punchitemid === p.dagrun_pm_punchitemid ? { ...x, ...patch } as PunchItem : x));
                    }}
                    style={{ fontSize: 11, padding: '2px 6px', border: 'none', background: STATUS_BADGE[p.dagrun_pm_status ?? PunchStatus.Open]?.bg, color: STATUS_BADGE[p.dagrun_pm_status ?? PunchStatus.Open]?.fg, borderRadius: 3, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {STATUS_ORDER.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
                  </select>
                </Td>
                <Td>{isPunchVerified(p)
                  ? <span title={p.dagrun_pm_verifiedby ? `Verified by ${p.dagrun_pm_verifiedby}${p.dagrun_pm_verifiedon ? ' · ' + new Date(p.dagrun_pm_verifiedon).toLocaleDateString() : ''}` : 'Verified'}
                      style={{ color: '#16A34A', fontSize: 15, fontWeight: 700 }}>✓</span>
                  : <Em />}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {drawerItem && (
        <PunchDrawer
          pca={pca}
          projectId={projectId}
          item={drawerItem === 'new' ? null : drawerItem}
          allItems={items}
          onClose={() => { setDrawerItem(null); reload(); }}
          onDeleted={() => { setDrawerItem(null); reload(); }}
        />
      )}
    </div>
  );
}

function SortableTh({ label, col, sortKey, sortDir, onSort }: {
  label: string; col: SortKey; sortKey: SortKey; sortDir: SortDir; onSort: (k: SortKey) => void;
}) {
  const active = sortKey === col;
  return (
    <th onClick={() => onSort(col)}
      style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: active ? '#0E7490' : '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label}{active && <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

const Td = ({ children, onClick }: { children: React.ReactNode; onClick?: (e: React.MouseEvent) => void }) => (
  <td onClick={onClick} style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = ({ children }: { children?: React.ReactNode }) => (
  <span style={{ color: '#B8BEC9', fontSize: 13 }}>{children ?? '—'}</span>
);

// ── Unified create/edit drawer ────────────────────────────────────────
// item === null → create mode (explicit Save button, auto item number).
// item !== null → edit mode (per-field auto-save PATCH).

function PunchDrawer({ pca, projectId, item, allItems, onClose, onDeleted }: {
  pca: IPublicClientApplication;
  projectId: string;
  item: PunchItem | null;
  allItems: PunchItem[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const isNew = item === null;
  const me = useCurrentUser(pca, projectId);

  // Local draft. For new items we keep a blank shell; for existing we copy the row.
  const blank: PunchItem = {
    dagrun_pm_punchitemid: '', dagrun_pm_name: '', dagrun_pm_projectid: projectId,
    dagrun_pm_status: PunchStatus.Open, dagrun_pm_severity: PunchSeverity.Minor,
  };
  const [draft, setDraft] = useState<PunchItem>(item ?? blank);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(item ?? blank); }, [item]); // eslint-disable-line react-hooks/exhaustive-deps

  // Approval stages (edit mode only — a record id is required).
  const [approvalStages, setApprovalStages] = useState<ApprovalStage[] | null>(null);
  const [stagesLoading, setStagesLoading] = useState(!isNew);
  const reloadStages = useCallback(async () => {
    if (isNew || !item) return;
    setStagesLoading(true);
    try { setApprovalStages(await getEntityStages(pca, item.dagrun_pm_punchitemid)); }
    finally { setStagesLoading(false); }
  }, [pca, item, isNew]);
  useEffect(() => { reloadStages(); }, [reloadStages]);

  const flashSaved = () => {
    setSavedFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
  };

  // Edit-mode auto-save.
  const patch = async (fields: Partial<PunchItem>) => {
    if (isNew || !item) { setDraft(d => ({ ...d, ...fields })); return; }
    setSaving(true);
    try {
      await updatePunchItem(pca, item.dagrun_pm_punchitemid, fields as Record<string, unknown>);
      flashSaved();
    } finally { setSaving(false); }
  };
  const setAndPatch = <K extends keyof PunchItem>(k: K, v: PunchItem[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    patch({ [k]: v } as Partial<PunchItem>);
  };
  const setText = <K extends keyof PunchItem>(k: K, v: PunchItem[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (isNew) return;
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<PunchItem>), 600);
  };
  const flush = <K extends keyof PunchItem>(k: K, v: PunchItem[K]) => {
    if (isNew) return;
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<PunchItem>);
  };

  // Status change — edit mode triggers the approval chain at Ready For Inspection,
  // and stamps verifiedon when reaching the verified (Closed) status.
  const handleStatusChange = async (next: number) => {
    const fields: Partial<PunchItem> = { dagrun_pm_status: next };
    if (next === PUNCH_VERIFIED_STATUS && !draft.dagrun_pm_verifiedon) {
      fields.dagrun_pm_verifiedon = new Date().toISOString();
      if (!draft.dagrun_pm_verifiedby && me?.displayName) fields.dagrun_pm_verifiedby = me.displayName;
    }
    setDraft(d => ({ ...d, ...fields }));
    if (isNew) return;
    await patch(fields);
    if (next === PunchStatus.ReadyForInspection && item) {
      const existing = approvalStages ?? [];
      if (existing.length === 0) {
        try {
          const chain = await findProjectChainForEntityType(pca, projectId, ApprovalEntityType.PunchItem);
          if (chain) {
            await instantiateStagesForEntity(pca, chain.dagrun_pm_approvalchainid, item.dagrun_pm_punchitemid, draft.dagrun_pm_name);
            reloadStages();
          }
        } catch { /* non-fatal */ }
      }
    }
  };

  const onStageDecision = async (stage: ApprovalStage, decision: 'approve' | 'reject', comments?: string) => {
    if (!item) return;
    try {
      if (decision === 'approve') await approveStage(pca, stage.dagrun_pm_approvalstageid, comments ?? null);
      else await rejectStage(pca, stage.dagrun_pm_approvalstageid, comments ?? '(rejected without comment)');
      const fresh = await getEntityStages(pca, item.dagrun_pm_punchitemid);
      setApprovalStages(fresh);
      if (fresh.length > 0) {
        const allApproved = fresh.every(s => s.dagrun_pm_status === ApprovalStageStatus.Approved);
        if (allApproved && draft.dagrun_pm_status !== PunchStatus.Closed) {
          const fields: Partial<PunchItem> = { dagrun_pm_status: PunchStatus.Closed };
          if (!draft.dagrun_pm_verifiedon) fields.dagrun_pm_verifiedon = new Date().toISOString();
          setDraft(d => ({ ...d, ...fields }));
          await patch(fields);
        }
      }
    } catch { /* best effort */ }
  };

  const createNew = async () => {
    if (!draft.dagrun_pm_name.trim() || saving) return;
    setSaving(true); setCreateError(null);
    try {
      await createPunchItem(pca, {
        projectId,
        name: draft.dagrun_pm_name.trim(),
        description: draft.dagrun_pm_description ?? null,
        location: draft.dagrun_pm_location ?? null,
        trade: draft.dagrun_pm_trade ?? null,
        drawingreference: draft.dagrun_pm_drawingreference ?? null,
        severity: draft.dagrun_pm_severity ?? PunchSeverity.Minor,
        status: draft.dagrun_pm_status ?? PunchStatus.Open,
        assigneeid: draft.dagrun_pm_assigneeid ?? null,
        assigneename: draft.dagrun_pm_assigneename ?? null,
        duedate: draft.dagrun_pm_duedate ?? null,
        notes: draft.dagrun_pm_notes ?? null,
        verifiedby: draft.dagrun_pm_verifiedby ?? null,
        verifiedon: draft.dagrun_pm_verifiedon ?? null,
        itemnumber: nextItemNumber(allItems),
      });
      onClose();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!item) return;
    setSaving(true);
    try { await deletePunchItem(pca, item.dagrun_pm_punchitemid); onDeleted(); } finally { setSaving(false); }
  };

  const verifyEnabled = draft.dagrun_pm_status === PUNCH_VERIFIED_STATUS;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 480,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px', fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8' }}>
            {isNew ? 'New punch item' : (draft.dagrun_pm_itemnumber != null ? `Item #${draft.dagrun_pm_itemnumber}` : '')}
            {!isNew && savedFlash && <span style={{ color: '#16A34A', marginLeft: 8 }}>Saved ✓</span>}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        {isNew ? (
          <input autoFocus value={draft.dagrun_pm_name} placeholder="Punch item title"
            onChange={e => setDraft(d => ({ ...d, dagrun_pm_name: e.target.value }))}
            style={{ ...BM_TITLE_INPUT, marginBottom: 16 }} />
        ) : (
          <input value={draft.dagrun_pm_name}
            onChange={e => setText('dagrun_pm_name', e.target.value)}
            onBlur={e => flush('dagrun_pm_name', e.target.value)}
            style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 16, width: '100%', background: 'transparent' }} />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Status</label>
            <select value={draft.dagrun_pm_status ?? PunchStatus.Open}
              onChange={e => handleStatusChange(Number(e.target.value))}
              style={BM_INPUT}>
              {STATUS_ORDER.map(v => <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
            </select>
          </div>
          <div>
            <label style={BM_LABEL}>Severity</label>
            <select value={draft.dagrun_pm_severity ?? PunchSeverity.Minor}
              onChange={e => setAndPatch('dagrun_pm_severity', Number(e.target.value))}
              style={BM_INPUT}>
              {SEVERITY_ORDER.map(v => <option key={v} value={v}>{SEVERITY_LABEL[v]}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Location</label>
            <input value={draft.dagrun_pm_location ?? ''}
              onChange={e => setText('dagrun_pm_location', e.target.value || null)}
              onBlur={e => flush('dagrun_pm_location', e.target.value || null)} style={BM_INPUT} />
          </div>
          <div>
            <label style={BM_LABEL}>Trade</label>
            <input value={draft.dagrun_pm_trade ?? ''}
              onChange={e => setText('dagrun_pm_trade', e.target.value || null)}
              onBlur={e => flush('dagrun_pm_trade', e.target.value || null)} style={BM_INPUT} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Drawing Reference</label>
            <input value={draft.dagrun_pm_drawingreference ?? ''}
              onChange={e => setText('dagrun_pm_drawingreference', e.target.value || null)}
              onBlur={e => flush('dagrun_pm_drawingreference', e.target.value || null)} style={BM_INPUT} placeholder="e.g. A-201" />
          </div>
          <div>
            <label style={BM_LABEL}>Due Date</label>
            <input type="date" value={draft.dagrun_pm_duedate?.slice(0, 10) ?? ''}
              onChange={e => setAndPatch('dagrun_pm_duedate', e.target.value || null)} style={BM_INPUT} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Assignee</label>
          <PeoplePicker pca={pca}
            value={draft.dagrun_pm_assigneeid ? { aadObjectId: draft.dagrun_pm_assigneeid, displayName: draft.dagrun_pm_assigneename ?? draft.dagrun_pm_assigneeid } : null}
            onChange={(p: Person | null) => {
              const fields = { dagrun_pm_assigneeid: p?.aadObjectId ?? null, dagrun_pm_assigneename: p?.displayName ?? null };
              setDraft(d => ({ ...d, ...fields }));
              patch(fields);
            }}
            placeholder="Assign to…" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Description</label>
          <textarea value={draft.dagrun_pm_description ?? ''}
            onChange={e => setText('dagrun_pm_description', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_description', e.target.value || null)} style={BM_TEXTAREA} />
        </div>

        {/* Verification — enabled only at the verified (Closed) status. */}
        <div style={{ marginBottom: 12, padding: '12px 14px', background: verifyEnabled ? '#F0FDF4' : '#F5F6F8', border: '0.5px solid #EAECF0', borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: verifyEnabled ? '#16A34A' : '#9198A8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Verification {verifyEnabled ? '' : '— set status to Verified to record'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={BM_LABEL}>Verified By</label>
              <input value={draft.dagrun_pm_verifiedby ?? ''} disabled={!verifyEnabled}
                onChange={e => setText('dagrun_pm_verifiedby', e.target.value || null)}
                onBlur={e => flush('dagrun_pm_verifiedby', e.target.value || null)}
                style={{ ...BM_INPUT, opacity: verifyEnabled ? 1 : 0.5 }} />
            </div>
            <div>
              <label style={BM_LABEL}>Verified On</label>
              <input type="date" disabled={!verifyEnabled}
                value={draft.dagrun_pm_verifiedon?.slice(0, 10) ?? ''}
                onChange={e => setAndPatch('dagrun_pm_verifiedon', e.target.value ? new Date(e.target.value).toISOString() : null)}
                style={{ ...BM_INPUT, opacity: verifyEnabled ? 1 : 0.5 }} />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Notes</label>
          <textarea value={draft.dagrun_pm_notes ?? ''}
            onChange={e => setText('dagrun_pm_notes', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_notes', e.target.value || null)} style={BM_TEXTAREA} />
        </div>

        {isNew ? (
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
            {createError && <span style={{ color: '#DC2626', fontSize: 12, marginRight: 'auto' }}>{createError}</span>}
            <BMCancelButton onClick={onClose} disabled={saving} />
            <BMPrimaryButton onClick={createNew} disabled={saving || !draft.dagrun_pm_name.trim()}>
              {saving ? 'Creating…' : 'Add Punch Item'}
            </BMPrimaryButton>
          </div>
        ) : (
          <>
            <ApprovalStagesSection
              pca={pca}
              stages={approvalStages}
              loading={stagesLoading}
              myOid={me?.oid}
              onDecision={onStageDecision}
              entityLabel="punch item"
              triggerStatusLabel="Ready For Inspection"
            />
            <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
              <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete</BMDangerButton>
            </div>
          </>
        )}
      </div>

      {confirmDelete && item && (
        <BoardModal open title="Delete Punch Item" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong>? This cannot be undone.
        </BoardModal>
      )}
    </>
  );
}
